import { useEffect, useState } from 'react';
import {
  DEFAULT_QTY,
  DOC_TIER1,
  DOC_TIER2,
  DOC_TIER3,
  DOC_TIER4,
  DOC_TIER5,
  DOC_TIER6,
  DOC_TIER7,
} from '../constants/spread';
import { getListPrice } from 'src/common/hooks/widgets/fetchData';
import { useCurrencyWithSymbol } from 'src/common/hooks/widgets/useCurrencyWithSymbol';
import { connection, map } from '../constants/default';

/**
 * Calculate the estimated price
 *
 * @param {string} localCurrency the language code to get the currency sign for
 * @param {number} connectionQty : Number of connection
 * @param {number} mapQty : Number of map
 * @param {number} documentQty : Number of document
 * @returns {Array}
 */

export interface PropsFunc {
  currency: string;
  country: string;
  localeCode: string;
  connectionQty: number;
  mapQty: number;
  documentQty: number;
  tileValueSelected: string;
}

export interface IFetchError {
  status: number;
  statusText: string;
}

export interface priceProps {
  partNumber: string;
  pricingIndicator: string;
  pricingModel: number;
  scaleQtyPrice: [
    {
      qty: number;
      price: number;
    },
  ];
}

const partNumber = {
  SetUp: 'D0H0FZX',
  Connection: 'D0H0CZX',
  Map: 'D0H0AZX',
  Document: 'D0H0EZX',
};

type typeOfPart = {
  qty: number;
  price: number;
};

const partsToQuery = [
  {
    partNumber: partNumber.SetUp,
  },
  {
    partNumber: partNumber.Connection,
  },
  {
    partNumber: partNumber.Map,
  },
  {
    partNumber: partNumber.Document,
  },
];

type MarketPlaceRequest = {
  currency: string;
  countryCode3: string;
  parts: typeof partsToQuery;
};

export const usePrice = ({
  currency,
  country,
  localeCode,
  connectionQty,
  mapQty,
  documentQty,
  tileValueSelected,
}: PropsFunc) => {
  const [totalCost1Y, setTotalCost1Y] = useCurrencyWithSymbol(
    currency,
    localeCode,
    0,
  );
  const [totalCost2Y, setTotalCost2Y] = useCurrencyWithSymbol(
    currency,
    localeCode,
    0,
  );
  const [priceSetUp, setPriceSetup] = useState(0);
  const [mapPrice, setMapPrice] = useState(0);
  const [connectionPrice, setConnectionPrice] = useState(0);
  const [docPriceTier, setDocPriceTier] = useState<number[]>([]);
  const [dataFetched, setDataFetched] = useState([]);
  const [fetchingError, setFetchingError] = useState({});
  const [loading, setLoading] = useState(false);
  const totalConnectionQty = connectionQty - connection.min;
  const totalMapQty = mapQty - map.min;
  const totalDocQty = documentQty / 1000;

  const getDocTierPrice = (docQty: number) => {
    let price = 0;
    switch (true) {
      case docQty === DOC_TIER1:
        price = docPriceTier[0];
        break;
      case docQty >= DOC_TIER2 && docQty < DOC_TIER3:
        price = docPriceTier[1];
        break;
      case docQty >= DOC_TIER3 && docQty < DOC_TIER4:
        price = docPriceTier[2];
        break;
      case docQty >= DOC_TIER4 && docQty < DOC_TIER5:
        price = docPriceTier[3];
        break;
      case docQty >= DOC_TIER5 && docQty < DOC_TIER6:
        price = docPriceTier[4];
        break;
      case docQty >= DOC_TIER6 && docQty < DOC_TIER7:
        price = docPriceTier[5];
        break;
      case docQty > DOC_TIER7:
        price = docPriceTier[6];
        break;
    }
    return price;
  };

  const calculatedPrice = (docPrice: number) => {
    const calcPrice =
      totalConnectionQty * connectionPrice +
      totalMapQty * mapPrice +
      totalDocQty * docPrice +
      priceSetUp;
    return calcPrice;
  };

  const setPricesData = (products: priceProps[]) => {
    const setUpPriceValue = getPrice(products, partNumber.SetUp, DEFAULT_QTY);
    const mapPriceValue = getPrice(products, partNumber.Map, DEFAULT_QTY);
    const connectionPriceValue = getPrice(
      products,
      partNumber.Connection,
      DEFAULT_QTY,
    );
    setPriceSetup(setUpPriceValue);
    setMapPrice(mapPriceValue);
    setConnectionPrice(connectionPriceValue);
    const docPriceTier1 = getPrice(products, partNumber.Document, DOC_TIER1);
    const docPriceTier2 = getPrice(products, partNumber.Document, DOC_TIER2);
    const docPriceTier3 = getPrice(products, partNumber.Document, DOC_TIER3);
    const docPriceTier4 = getPrice(products, partNumber.Document, DOC_TIER4);
    const docPriceTier5 = getPrice(products, partNumber.Document, DOC_TIER5);
    const docPriceTier6 = getPrice(products, partNumber.Document, DOC_TIER6);
    const docPriceTier7 = getPrice(products, partNumber.Document, DOC_TIER7);
    const docTierPriceValue = [
      docPriceTier1,
      docPriceTier2,
      docPriceTier3,
      docPriceTier4,
      docPriceTier5,
      docPriceTier6,
      docPriceTier7,
    ];
    setDocPriceTier(docTierPriceValue);
  };

  const getPrice = (
    priceFile: priceProps[],
    partNumber: string,
    quantity: number,
  ) => {
    let price = 0;

    let value = {} as typeOfPart | undefined;
    // Find product
    if (priceFile?.length) {
      const listPrice = priceFile.find((i) => i.partNumber == partNumber);
      if (listPrice) {
        value = listPrice.scaleQtyPrice.find((i) => i.qty === quantity);
        if (value) {
          price = Math.round(value?.price);
        }
      }
    }
    return price;
  };

  const fetchPrices = () => {
    setLoading(true);
    const body: MarketPlaceRequest = {
      currency,
      countryCode3: country,
      parts: partsToQuery,
    };
    getListPrice(
      `https://api.marketplace.ibm.com/purchase/catalog/resources/price/info`,
      JSON.stringify(body),
      'POST',
    )
      .then((data) => {
        if (data.parts && data.parts.length !== 0) {
          setLoading(false);
          setPricesData(data.parts);
          setDataFetched(data.parts);
        } else {
          setLoading(false);
          setDataFetched([]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setDataFetched([]);
        if (error.response) {
          setFetchingError(error.response);
        }
      });
  };
  const docPrice = getDocTierPrice(totalDocQty);
  const price = calculatedPrice(docPrice);

  useEffect(() => {
    if (currency && country && dataFetched.length === 0) {
      fetchPrices();
    }
  }, [currency, localeCode, country]);

  useEffect(() => {
    setTotalCost1Y(price);
    setTotalCost2Y(totalDocQty * docPrice);
  }, [connectionQty, mapQty, documentQty, tileValueSelected, price]);

  return [totalCost1Y, totalCost2Y, loading, fetchingError];
};
